import { BlendMode } from "../type";

// 블룸 합성 셰이더 정의
export const bloomShader = {
  uniforms: {
    baseTexture: { value: null },
    bloomTexture: { value: null },
    bloomStrength: { value: 1.0 },
    blendOpacity: { value: 0.1 },
    applyGamma: { value: 0.0 },
    blendMode: { value: 1 }
  },

  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: `
    uniform sampler2D baseTexture;
    uniform sampler2D bloomTexture;
    uniform float bloomStrength;
    uniform float blendOpacity;
    uniform float applyGamma;
    uniform int blendMode;
    
    varying vec2 vUv;
    
    // 감마 보정 유틸리티 함수
    vec3 gammaCorrect(vec3 color) {
      return pow(color, vec3(1.0/2.2));
    }
    
    vec3 gammaReverse(vec3 color) {
      return pow(color, vec3(2.2));
    }
    
    // 블렌드 모드에 따른 색상 혼합 함수
    vec3 blendColors(vec3 base, vec3 blend, int mode) {
      // 0: Normal, 1: Add, 2: Screen, 3: Multiply, 4: Overlay
      if (mode == 1) {
        // Add 블렌드 모드
        return base + blend;
      }
      else if (mode == 2) {
        // Screen 블렌드 모드: 색상을 밝게 만듦
        return 1.0 - (1.0 - base) * (1.0 - blend);
      }
      else if (mode == 3) {
        // Multiply 블렌드 모드: 색상을 어둡게 만듦
        return base * blend;
      }
      else if (mode == 4) {
        // Overlay 블렌드 모드: 대비 증가
        vec3 result;
        for (int i = 0; i < 3; i++) {
          if (base[i] < 0.5) {
            result[i] = 2.0 * base[i] * blend[i];
          } else {
            result[i] = 1.0 - 2.0 * (1.0 - base[i]) * (1.0 - blend[i]);
          }
        }
        return result;
      }
      // 기본: Normal 블렌드 모드
      return blend;
    }
    
    void main() {
      vec4 baseColor = texture2D(baseTexture, vUv);
      vec4 bloomColor = texture2D(bloomTexture, vUv);
      
      // 블룸 강도 조절
      vec3 bloomFactor = bloomColor.rgb * bloomStrength;
      
      // 블룸이 있는 영역 감지 (임계값 적용)
      if (length(bloomColor.rgb) > 0.0) {
        // 감마 보정 적용 여부에 따라 처리
        vec3 finalColor;
        
        if (applyGamma > 0.5) {
          // 감마 보정 적용
          vec3 baseLinear = gammaReverse(baseColor.rgb);
          vec3 bloomWithBase = blendColors(baseLinear, bloomFactor, blendMode);
          finalColor = gammaCorrect(bloomWithBase);
        } else {
          // 감마 보정 없이 직접 혼합
          finalColor = blendColors(baseColor.rgb, bloomFactor, blendMode);
        }
        
        // 최종 혼합 투명도 적용
        gl_FragColor = vec4(mix(baseColor.rgb, finalColor, blendOpacity), baseColor.a);
      } else {
        gl_FragColor = baseColor;
      }
    }
  `
};

// 블룸 전용 표시 셰이더 (디버깅용)
export const bloomOnlyShader = `
  uniform sampler2D baseTexture;
  uniform sampler2D bloomTexture;
  uniform float bloomStrength;
  varying vec2 vUv;
  
  void main() {
    vec4 baseColor = texture2D(baseTexture, vUv);
    vec4 bloomColor = texture2D(bloomTexture, vUv);
    
    float luminance = length(bloomColor.rgb);
    
    if (luminance > 0.01) {
      // 블룸이 있는 부분은 원래 텍스처 + 강조된 블룸 효과 표시
      vec3 highlightColor = bloomColor.rgb * bloomStrength * 2.0; // 더 밝게 강조
      gl_FragColor = vec4(baseColor.rgb + highlightColor, baseColor.a);
    } else {
      // 블룸이 없는 부분은 어둡게 하되, 완전히 보이지 않게 하지는 않음
      gl_FragColor = vec4(baseColor.rgb * 0.2, baseColor.a); // 어둡게 처리
    }
  }
`;

// 기본 씬 전용 표시 셰이더 (디버깅용)
export const baseOnlyShader = `
  uniform sampler2D baseTexture;
  uniform sampler2D bloomTexture;
  varying vec2 vUv;
  void main() {
    // 기본 씬만 표시
    gl_FragColor = texture2D(baseTexture, vUv);
  }
`;

// 블렌드 모드를 셰이더에서 사용할 정수 값으로 변환
export function getBlendModeValue(mode: BlendMode): number {
  const modes: Record<BlendMode, number> = {
    'normal': 0,
    'add': 1,
    'screen': 2,
    'multiply': 3,
    'overlay': 4
  };
  return modes[mode] || 1; // 기본값은 add (1)
} 